import { useState } from 'react';
import {
  Color, Size, TEXTED_CARDS_PATH, TEXTED_CARD_PERSON_FIELDS, t, textedCardDismissPath, textedCardFrom, textedCardLeadValues,
  textedCardPersonValues, textedCardSummary, type TextedCardOfferRow, type TextedCardRecordType, type TextedCardsAnswer,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { NewRecordModal } from './NewRecordModal';
import { post, useApi } from './api';
import styles from './TextedCardOffers.module.css';

/** Which form an offer opened, and from which offer. */
interface Opened {
  offer: TextedCardOfferRow;
  recordType: TextedCardRecordType;
}

export interface TextedCardOffersProps {
  apiBaseUrl?: string;
  /** Told when a Lead or a Person was made from a card, so the list reads again and the record can open. */
  onCreated: (created: { recordType: TextedCardRecordType; id: string }) => void;
}

/**
 * 🔒 The texted-card offers on the Leads page (piece 20; decisions 0202 and
 * 5180). A row per open offer reads "Texted card from" the number, with the
 * card's name, title and company beneath, and three acts: Create Lead and
 * Create Person at Company each open the existing form pre-filled from the
 * card, and Dismiss closes the offer. 🔨 The card's words are drawn as text
 * and nothing else of the card reaches the page. A viewer who cannot read
 * Leads, or a page with no open offer, draws nothing here.
 */
export function TextedCardOffers({ apiBaseUrl = '', onCreated }: TextedCardOffersProps) {
  const { state, reload } = useApi<TextedCardsAnswer>(TEXTED_CARDS_PATH, apiBaseUrl);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [notice, setNotice] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);
  const answer = state.kind === 'ready' ? state.data : null;
  const rows = answer?.rows ?? [];

  const dismiss = async (offer: TextedCardOfferRow) => {
    const outcome = await post(textedCardDismissPath(offer.id), {}, apiBaseUrl);
    setNotice(outcome.ok ? { tone: 'good', text: t('lead.textedCard.dismissed') } : { tone: 'error', text: outcome.message || t('lead.textedCard.failed') });
    reload();
  };

  /**
   * 🔒 The record is saved and the offer taken by it in one request (decision 5180): the create named the offer,
   * and its own transaction wrote the record's line naming the text. An offer answered meanwhile refuses the
   * save inside the form, with nothing made.
   */
  const taken = (from: Opened, created: { id: string }) => {
    setOpened(null);
    setNotice({ tone: 'good', text: t('lead.textedCard.taken') });
    reload();
    onCreated({ recordType: from.recordType, id: created.id });
  };

  if (rows.length === 0 && !notice) return null;
  return (
    <>
      {notice ? <AlertBanner tone={notice.tone} message={notice.text} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
      {rows.length > 0 ? (
        <section className={styles.offers} data-texted-card-offers={rows.length} aria-label={t('lead.textedCard.title')}>
          <h2 className={styles.title}>{t('lead.textedCard.title')}</h2>
          <ul className={styles.list}>
            {rows.map((offer) => (
              <li key={offer.id} className={styles.row} data-texted-card={offer.id}>
                <span className={styles.words}>
                  <span className={styles.from}>{t('lead.textedCard.from')} {textedCardFrom(offer.fromNumber)}</span>
                  <span className={styles.card}>{textedCardSummary(offer.card)}</span>
                </span>
                {answer?.mayAct ? (
                  <span className={styles.acts}>
                    <Button size={Size.Small} color={Color.Navy} text={[t('lead.textedCard.createLead')]} actions={[() => setOpened({ offer, recordType: 'Lead' })]} />
                    <Button size={Size.Small} color={Color.Navy} text={[t('lead.textedCard.createPerson')]} actions={[() => setOpened({ offer, recordType: 'PersonRecord' })]} />
                    <Button size={Size.Small} color={Color.Inactive} text={[t('lead.textedCard.dismiss')]} actions={[() => void dismiss(offer)]} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {/* 🔨 Mounted only while open and keyed to the offer and the act, so each opening reads its own card (CLAUDE.md, the hidden modal's initial prop). */}
      {opened ? (
        <NewRecordModal
          key={`${opened.offer.id}-${opened.recordType}`}
          open
          recordType={opened.recordType}
          apiBaseUrl={apiBaseUrl}
          openedFrom="new"
          initialValues={opened.recordType === 'Lead' ? textedCardLeadValues(opened.offer) : textedCardPersonValues(opened.offer)}
          {...(opened.recordType === 'PersonRecord' ? { extraFields: TEXTED_CARD_PERSON_FIELDS, lead: opened.offer.company ? t('lead.textedCard.personCompany') : t('lead.textedCard.personLead') } : {})}
          onCancel={() => setOpened(null)}
          sendAlso={{ textedCardOfferId: opened.offer.id }}
          onCreated={(created) => taken(opened, created)}
        />
      ) : null}
    </>
  );
}
